import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  Bot,
  Building2,
  FileText,
  HeartPulse,
  Search,
  StickyNote,
  UsersRound,
  Pencil,
} from "lucide-react";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { api, formatApiError } from "@/lib/api";
import { ClientOperationsEditor } from "@/components/ClientOperationsEditor";
import { PlannedRosterPanel } from "@/components/operations/PlannedRosterPanel";
import { useAuth } from "@/context/AuthContext";

const apiUrl = process.env.REACT_APP_BACKEND_URL;
const healthBriefEmptyText =
  "Generate an operational brief when you need a concise next-step readout.";
const contractsEmptyText = "No uploaded contracts or amendments are linked to this client yet.";

function currency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function formatMargin(value) {
  return value === null ? "—" : `${value}%`;
}

function HealthBadge({ health }) {
  return (
    <span
      className={`operations-health-badge operations-health-${health.color}`}
      data-testid={`health-level-${health.level.toLowerCase().replaceAll(" ", "-")}`}
    >
      {health.level}
    </span>
  );
}

export default function OperationsCenterPage() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [coverageWarnings, setCoverageWarnings] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    state: "",
    service_type: "",
    account_manager: "",
  });
  const [note, setNote] = useState("");
  const [explanation, setExplanation] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");
  const [isEditingClient, setIsEditingClient] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      const response = await api.get("/operations-center", { params: filters });
      setDashboard(response.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
    try {
      setCoverageWarnings((await api.get("/attendance/coverage-warnings")).data);
    } catch (requestError) {
      setCoverageWarnings([]);
    }
  }, [filters]);

  async function openClient(clientId) {
    try {
      const response = await api.get(`/operations-center/${clientId}`);
      setSelectedClient(response.data);
      setExplanation("");
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  async function updateFilter(name, value) {
    const nextFilters = { ...filters, [name]: value };
    setFilters(nextFilters);
  }

  async function addNote(event) {
    event.preventDefault();
    if (!selectedClient || !note.trim()) {
      return;
    }
    try {
      await api.post(`/operations-center/${selectedClient.id}/notes`, { content: note });
      setNote("");
      await openClient(selectedClient.id);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function generateExplanation() {
    if (!selectedClient) {
      return;
    }
    setExplanation("");
    setIsGenerating(true);
    try {
      const token = window.localStorage.getItem("tagforce-access-token");
      const response = await fetch(
        `${apiUrl}/api/operations-center/${selectedClient.id}/health-explanation`,
        {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!response.ok || !response.body) {
        throw new Error("We could not generate the health explanation.");
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let pending = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        pending += decoder.decode(value, { stream: true });
        const events = pending.split("\n\n");
        pending = events.pop() || "";
        events.forEach((entry) => {
          const dataLine = entry.split("\n").find((line) => line.startsWith("data: "));
          if (!dataLine) {
            return;
          }
          const payload = JSON.parse(dataLine.replace("data: ", ""));
          if (payload.delta) {
            setExplanation((current) => `${current}${payload.delta}`);
          }
        });
      }
    } catch (requestError) {
      setError(requestError.message || "We could not generate the health explanation.");
    } finally {
      setIsGenerating(false);
    }
  }

  const selectedTrend = useMemo(() => {
    return (selectedClient?.health_trend || []).map((item) => ({
      ...item,
      day: item.snapshot_day?.slice(5) || "Today",
    }));
  }, [selectedClient]);

  return (
    <div className="app-shell" data-testid="operations-center-shell">
      <AppSidebar activeItem="Operations Center" />
      <div className="app-content">
        <AppHeader />
        <main
          className="dashboard-page operations-center-page"
          data-testid="operations-center-page"
        >
          <section className="page-intro operations-intro">
            <div>
              <p className="eyebrow">RELATIONSHIP DELIVERY CONTROL</p>
              <h1>School Operations Center</h1>
              <p className="page-subtitle">
                Live client health across coverage, commercial outlook, credentials, and delivery
                signals.
              </p>
            </div>
            <div className="operations-legend" data-testid="operations-health-legend">
              <span>
                <i className="health-dot health-dot-green" />
                Healthy
              </span>
              <span>
                <i className="health-dot health-dot-yellow" />
                Attention
              </span>
              <span>
                <i className="health-dot health-dot-red" />
                Critical
              </span>
            </div>
          </section>

          {error && (
            <p className="form-message error-message" data-testid="operations-center-error">
              {error}
            </p>
          )}

          {!dashboard && !error && (
            <p className="projection-loading" data-testid="operations-center-loading">
              Mapping client operations…
            </p>
          )}

          {dashboard && (
            <>
              {coverageWarnings.length > 0 && (
                <section className="operations-coverage-warnings" data-testid="operations-coverage-warnings" aria-live="polite">
                  {coverageWarnings.map((warning) => (
                    <p key={warning.client_id} className="operations-coverage-warning" data-testid={`coverage-warning-${warning.client_id}`}>
                      <AlertTriangle size={15} /> <span>{warning.message}</span>
                      <small className="pairing-meta">{warning.active_placements} active placement{warning.active_placements === 1 ? "" : "s"}{warning.site_mapped ? "" : " · site not mapped in Connecteam → Sites"}</small>
                    </p>
                  ))}
                </section>
              )}
              <section className="operations-summary-grid" data-testid="operations-summary-grid">
                <SummaryMetric
                  icon={Building2}
                  label="Client relationships"
                  value={dashboard.summary.client_count}
                  testId="operations-client-count"
                />
                <SummaryMetric
                  icon={HeartPulse}
                  label="Healthy"
                  value={dashboard.summary.healthy_count}
                  tone="green"
                  testId="operations-healthy-count"
                />
                <SummaryMetric
                  icon={Activity}
                  label="Attention needed"
                  value={dashboard.summary.attention_count}
                  tone="yellow"
                  testId="operations-attention-count"
                />
                <SummaryMetric
                  icon={ArrowUpRight}
                  label="Critical"
                  value={dashboard.summary.critical_count}
                  tone="red"
                  testId="operations-critical-count"
                />
              </section>

              <section className="operations-filter-bar" data-testid="operations-filter-bar">
                <label className="operations-search-label">
                  <Search size={16} aria-hidden="true" />
                  <input
                    data-testid="operations-search-input"
                    placeholder="Search school, district, state, service, or account manager"
                    value={filters.search}
                    onChange={(event) => updateFilter("search", event.target.value)}
                  />
                </label>
                <select
                  data-testid="operations-state-filter"
                  value={filters.state}
                  onChange={(event) => updateFilter("state", event.target.value)}
                >
                  <option value="">All states</option>
                  {dashboard.filters.states.map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
                <select
                  data-testid="operations-service-filter"
                  value={filters.service_type}
                  onChange={(event) => updateFilter("service_type", event.target.value)}
                >
                  <option value="">All service lines</option>
                  {dashboard.filters.service_lines.map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
                <select
                  data-testid="operations-manager-filter"
                  value={filters.account_manager}
                  onChange={(event) => updateFilter("account_manager", event.target.value)}
                >
                  <option value="">All account managers</option>
                  {dashboard.filters.account_managers.map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </section>

              <section className="operations-client-grid" data-testid="operations-client-grid">
                {dashboard.clients.map((client) => (
                  <article
                    className="operations-client-card"
                    data-testid={`operations-client-${client.id}`}
                    key={client.id}
                  >
                    <div className="operations-client-card-topline">
                      <p>{client.state}</p>
                      <HealthBadge health={client.health} />
                    </div>
                    <h2>{client.name}</h2>
                    <p className="operations-client-manager">
                      Account manager · {client.account_manager}
                    </p>
                    <div className="operations-score-row">
                      <strong data-testid={`client-health-score-${client.id}`}>
                        {client.health.score}
                      </strong>
                      <span>Client Health Score</span>
                    </div>
                    <div className="operations-mini-metrics">
                      <span data-testid={`client-fill-label-${client.id}`}>
                        <b>{client.fill_label}</b>
                      </span>
                      <span>
                        <b>{formatMargin(client.profit_margin)}</b> margin
                      </span>
                    </div>
                    {client.unfilled_roles?.length > 0 && (
                      <ul className="operations-unfilled-roles" data-testid={`client-unfilled-roles-${client.id}`}>
                        {client.unfilled_roles.map((role) => (
                          <li key={role.id}>Unfilled: {role.label}</li>
                        ))}
                      </ul>
                    )}
                    <div className="operations-service-tags">
                      {client.service_lines.slice(0, 4).map((line) => (
                        <span key={line}>{line}</span>
                      ))}
                      {!client.service_lines.length && <span>No service lines recorded</span>}
                    </div>
                    <button
                      className="operations-link-button"
                      data-testid={`open-operations-client-${client.id}`}
                      type="button"
                      onClick={() => openClient(client.id)}
                    >
                      Open relationship workspace <ArrowUpRight size={15} />
                    </button>
                  </article>
                ))}
                {!dashboard.clients.length && (
                  <p className="operations-empty-state" data-testid="operations-client-empty-state">
                    No client relationships match these filters.
                  </p>
                )}
              </section>
            </>
          )}

          {selectedClient && (
            <section className="operations-detail" data-testid="operations-client-detail">
              <div className="operations-detail-heading">
                <div>
                  <p className="eyebrow">CLIENT RELATIONSHIP WORKSPACE</p>
                  <h2>{selectedClient.name}</h2>
                  <p>{selectedClient.profile}</p>
                </div>
                <div className="operations-detail-score">
                  {user?.role === "system_administrator" && (
                    <button
                      className="button button-secondary"
                      data-testid="edit-operations-client-button"
                      type="button"
                      onClick={() => setIsEditingClient(true)}
                    >
                      <Pencil size={15} /> Edit client operations
                    </button>
                  )}
                  <HealthBadge health={selectedClient.health} />
                  <strong data-testid="selected-client-health-score">
                    {selectedClient.health.score}/100
                  </strong>
                </div>
              </div>

              {isEditingClient && (
                <ClientOperationsEditor
                  client={selectedClient}
                  onClose={() => setIsEditingClient(false)}
                  onSaved={() => openClient(selectedClient.id)}
                />
              )}

              <div className="operations-detail-grid">
                <DetailPanel
                  icon={UsersRound}
                  title="Primary contacts"
                  testId="operations-primary-contacts"
                >
                  {selectedClient.primary_contacts.map((contact) => (
                    <p key={contact.id}>
                      <b>
                        {contact.first_name} {contact.last_name}
                      </b>
                      <span>{contact.email}</span>
                    </p>
                  ))}
                  {!selectedClient.primary_contacts.length && (
                    <EmptyCopy text="No client-account contact is linked yet." />
                  )}
                </DetailPanel>
                <DetailPanel
                  icon={FileText}
                  title="Commercial outlook"
                  testId="operations-commercial-outlook"
                >
                  <p>
                    <b>{currency(selectedClient.monthly_revenue)}</b>
                    <span>Monthly modeled revenue</span>
                  </p>
                  <p>
                    <b>
                      {selectedClient.renewal_days === null
                        ? "—"
                        : `${selectedClient.renewal_days} days`}
                    </b>
                    <span>Nearest renewal</span>
                  </p>
                  <p>
                    <b>{currency(selectedClient.outstanding_invoices)}</b>
                    <span>Outstanding invoices</span>
                  </p>
                </DetailPanel>
                <DetailPanel
                  icon={Activity}
                  title="Service utilization"
                  testId="operations-service-utilization"
                >
                  <p>
                    <b>{selectedClient.contracted_hours.toLocaleString()} hrs</b>
                    <span>Contracted weekly capacity</span>
                  </p>
                  <p>
                    <b>Not tracked</b>
                    <span>Delivered and remaining hours await time records</span>
                  </p>
                </DetailPanel>
                <DetailPanel
                  icon={UsersRound}
                  title="Provider roster & compliance"
                  testId="operations-provider-roster"
                >
                  <p>
                    <b>{selectedClient.provider_roster.length}</b>
                    <span>Active provider placements</span>
                  </p>
                  <p>
                    <b>{selectedClient.credential_compliance.status}</b>
                    <span>
                      {selectedClient.credential_compliance.critical} critical credential alerts
                    </span>
                  </p>
                </DetailPanel>
              </div>

              <div className="operations-health-workspace">
                <PlannedRosterPanel clientId={selectedClient.id} canEdit={["system_administrator", "operations"].includes(user?.role)} onSaved={() => { openClient(selectedClient.id); loadDashboard(); }} />
                <section className="operations-health-factors" data-testid="client-health-factors">
                  <div className="operations-panel-heading">
                    <HeartPulse size={18} />
                    <div>
                      <h3>Health score factors</h3>
                      <p>Deterministic, live operational inputs.</p>
                    </div>
                  </div>
                  {selectedClient.health.factors.map((factor) => (
                    <div className="operations-factor" key={factor.label}>
                      <div>
                        <span>{factor.label}</span>
                        <b>{factor.value}</b>
                      </div>
                      <div className="operations-factor-track">
                        <i style={{ width: `${(factor.score / factor.max_score) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </section>
                <section className="operations-health-trend" data-testid="client-health-trend">
                  <div className="operations-panel-heading">
                    <Activity size={18} />
                    <div>
                      <h3>Health trend</h3>
                      <p>Snapshots begin when this workspace is used.</p>
                    </div>
                  </div>
                  <div className="operations-trend-chart">
                    {selectedTrend.length > 0 ? (
                      <ResponsiveContainer height="100%" width="100%">
                        <LineChart
                          data={selectedTrend}
                          margin={{ top: 8, right: 8, left: -28, bottom: 0 }}
                        >
                          <XAxis dataKey="day" tick={{ fill: "#9bb8bc", fontSize: 10 }} />
                          <YAxis domain={[0, 100]} tick={{ fill: "#9bb8bc", fontSize: 10 }} />
                          <Tooltip />
                          <Line dataKey="score" stroke="#d4af37" strokeWidth={3} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <EmptyCopy text="No historical health snapshots have been captured yet." />
                    )}
                  </div>
                </section>
              </div>

              <div className="operations-action-grid">
                <section className="operations-ai-panel" data-testid="client-health-ai-panel">
                  <div className="operations-panel-heading">
                    <Bot size={18} />
                    <div>
                      <h3>AI relationship brief</h3>
                      <p>Uses the deterministic health factors above.</p>
                    </div>
                  </div>
                  {explanation && <p data-testid="client-health-ai-response">{explanation}</p>}
                  {!explanation && !isGenerating && <EmptyCopy text={healthBriefEmptyText} />}
                  <button
                    className="button button-secondary"
                    data-testid="generate-client-health-explanation-button"
                    disabled={isGenerating}
                    type="button"
                    onClick={generateExplanation}
                  >
                    <Bot size={15} /> {isGenerating ? "Preparing brief…" : "Generate brief"}
                  </button>
                </section>
                <section
                  className="operations-recommendations"
                  data-testid="client-health-recommendations"
                >
                  <div className="operations-panel-heading">
                    <ArrowUpRight size={18} />
                    <div>
                      <h3>Recommended actions</h3>
                      <p>Prioritized from current delivery signals.</p>
                    </div>
                  </div>
                  <ol>
                    {selectedClient.health.recommendations.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ol>
                </section>
              </div>

              <div className="operations-record-grid">
                <DetailPanel
                  icon={FileText}
                  title="Contracts & amendments"
                  testId="operations-contracts"
                >
                  {selectedClient.contracts.map((contract) => (
                    <p key={contract.id}>
                      <b>{contract.contract_name || contract.original_filename}</b>
                      <span>{contract.status}</span>
                    </p>
                  ))}
                  {selectedClient.amendments.map((contract) => (
                    <p key={contract.id}>
                      <b>Amendment · {contract.contract_name || contract.original_filename}</b>
                      <span>{contract.status}</span>
                    </p>
                  ))}
                  {!selectedClient.contracts.length && !selectedClient.amendments.length && (
                    <EmptyCopy text={contractsEmptyText} />
                  )}
                </DetailPanel>
                <DetailPanel
                  icon={Activity}
                  title="IEP-heavy periods"
                  testId="operations-iep-periods"
                >
                  {selectedClient.iep_heavy_periods.map((period) => (
                    <p key={period}>
                      <b>{period}</b>
                    </p>
                  ))}
                  {!selectedClient.iep_heavy_periods.length && (
                    <EmptyCopy text="No IEP-heavy periods have been recorded yet." />
                  )}
                </DetailPanel>
                <section className="operations-notes-panel" data-testid="operations-internal-notes">
                  <div className="operations-panel-heading">
                    <StickyNote size={18} />
                    <div>
                      <h3>Internal notes</h3>
                      <p>Visible to internal operations roles only.</p>
                    </div>
                  </div>
                  <form onSubmit={addNote}>
                    <textarea
                      data-testid="operations-note-input"
                      placeholder="Add an internal relationship note"
                      rows="3"
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                    />
                    <button
                      className="button button-primary"
                      data-testid="save-operations-note-button"
                    >
                      Save note
                    </button>
                  </form>
                  <div className="operations-note-list">
                    {selectedClient.notes.map((item) => (
                      <p key={item.id}>
                        <b>{item.author_name}</b>
                        {item.content}
                      </p>
                    ))}
                    {!selectedClient.notes.length && (
                      <EmptyCopy text="No internal notes have been added yet." />
                    )}
                  </div>
                </section>
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

function SummaryMetric({ icon: Icon, label, value, tone = "teal", testId }) {
  return (
    <article
      className={`operations-summary-metric operations-summary-${tone}`}
      data-testid={testId}
    >
      <Icon size={18} aria-hidden="true" />
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function DetailPanel({ children, icon: Icon, title, testId }) {
  return (
    <section className="operations-detail-panel" data-testid={testId}>
      <div className="operations-panel-heading">
        <Icon size={18} />
        <h3>{title}</h3>
      </div>
      <div className="operations-detail-panel-body">{children}</div>
    </section>
  );
}

function EmptyCopy({ text }) {
  return (
    <p className="operations-empty-copy" data-testid="operations-empty-copy">
      {text}
    </p>
  );
}
